import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { API } from "@/lib/api";


const inputCls =
    "w-full rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none";

export default function ProfileTab() {
    const { user, setUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [saving, setSaving] = useState(false);
    const [pwSaving, setPwSaving] = useState(false);
    const [phone, setPhone] = useState(user?.phone || "");
    const [smsOptIn, setSmsOptIn] = useState(!!user?.sms_opt_in);
    const [smsSaving, setSmsSaving] = useState(false);
    const [smsTesting, setSmsTesting] = useState(false);
    const isEmail = user?.auth_provider === "email";

    const saveName = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/auth/profile`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: name.trim() }),
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            setUser(d);
            toast.success("Profile saved");
        } catch {
            toast.error("Could not save profile");
        } finally {
            setSaving(false);
        }
    };

    const saveSms = async () => {
        setSmsSaving(true);
        try {
            const r = await fetch(`${API}/auth/notifications`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone: phone.trim(), sms_opt_in: smsOptIn }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setUser((u) => ({ ...u, phone: d.phone, sms_opt_in: d.sms_opt_in }));
            toast.success(d.sms_opt_in ? "SMS alerts enabled" : "SMS preference saved");
        } catch (e) {
            toast.error("Could not save SMS settings", { description: String(e.message) });
        } finally {
            setSmsSaving(false);
        }
    };

    const sendTestSms = async () => {
        setSmsTesting(true);
        try {
            const r = await fetch(`${API}/auth/notifications/test-sms`, {
                method: "POST",
                credentials: "include",
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            toast.success("Test text sent — check your phone");
        } catch (e) {
            toast.error("Test text failed", { description: String(e.message) });
        } finally {
            setSmsTesting(false);
        }
    };

    const changePassword = async () => {
        setPwSaving(true);
        try {
            const r = await fetch(`${API}/auth/change-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ current_password: current, new_password: next }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setCurrent("");
            setNext("");
            toast.success("Password updated");
        } catch (e) {
            toast.error("Could not update password", { description: String(e.message) });
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <div data-testid="settings-profile-tab" className="space-y-4">
            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Profile
                </h3>
                <div className="mt-5 grid max-w-md gap-3">
                    <label className="block">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Display name
                        </span>
                        <input
                            data-testid="profile-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={inputCls}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Email
                        </span>
                        <input
                            data-testid="profile-email-input"
                            value={user?.email || ""}
                            readOnly
                            className={`${inputCls} cursor-not-allowed opacity-60`}
                        />
                    </label>
                    <div className="flex items-center gap-2 font-mono text-[10px] text-slate-500">
                        <span className="rounded border border-slate-700 px-1.5 py-px uppercase tracking-wider">
                            {user?.auth_provider || "email"}
                        </span>
                        <span
                            data-testid="profile-plan-badge"
                            className="rounded border border-cyan-500/30 bg-cyan-950/40 px-1.5 py-px uppercase tracking-wider text-cyan-300"
                        >
                            {user?.plan || "developer"} plan
                        </span>
                    </div>
                    <button
                        data-testid="profile-save-button"
                        onClick={saveName}
                        disabled={saving || !name.trim()}
                        className="mt-1 w-fit rounded-md bg-cyan-400 px-5 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save profile"}
                    </button>
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    SMS notifications
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-400">
                    Get webhook delivery alerts, endpoint and service status, security alerts, and
                    account notifications by text. Optional — your account works fine without it.
                </p>
                <div className="mt-5 grid max-w-md gap-3">
                    <label className="block">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Mobile phone
                        </span>
                        <input
                            data-testid="sms-phone-input"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+1 555 123 4567"
                            className={inputCls}
                        />
                    </label>
                    <label className="flex items-start gap-2.5">
                        <input
                            data-testid="sms-optin-checkbox"
                            type="checkbox"
                            checked={smsOptIn}
                            onChange={(e) => setSmsOptIn(e.target.checked)}
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-cyan-400"
                        />
                        <span className="text-[11px] font-semibold leading-relaxed text-slate-300">
                            Yes — text me MDH-API alerts
                        </span>
                    </label>
                    <p
                        data-testid="sms-disclosure"
                        className="text-[10px] leading-relaxed text-slate-500"
                    >
                        Message frequency varies. Message and data rates may apply. Reply STOP to
                        opt out or HELP for assistance. See our{" "}
                        <Link to="/terms" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                            Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                            Privacy Policy
                        </Link>
                        .
                        {user?.sms_opt_in && user?.sms_consent_at && (
                            <>
                                {" "}Opted in {new Date(user.sms_consent_at).toLocaleString()}.
                            </>
                        )}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="sms-save-button"
                            onClick={saveSms}
                            disabled={smsSaving || (smsOptIn && !phone.trim())}
                            className="w-fit rounded-md border border-cyan-500/40 bg-cyan-950/40 px-5 py-2.5 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                        >
                            {smsSaving ? "Saving…" : "Save SMS settings"}
                        </button>
                        {user?.sms_opt_in && (
                            <button
                                data-testid="sms-test-button"
                                onClick={sendTestSms}
                                disabled={smsTesting}
                                className="w-fit rounded-md border border-slate-700 px-5 py-2.5 font-mono text-[11px] text-slate-400 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-50"
                            >
                                {smsTesting ? "Sending…" : "Send test text"}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Password
                </h3>
                {isEmail ? (
                    <div className="mt-5 grid max-w-md gap-3">
                        <input
                            data-testid="password-current-input"
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            placeholder="Current password"
                            className={inputCls}
                        />
                        <input
                            data-testid="password-new-input"
                            type="password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            placeholder="New password (8+ characters)"
                            className={inputCls}
                        />
                        <button
                            data-testid="password-change-button"
                            onClick={changePassword}
                            disabled={pwSaving || !current || next.length < 8}
                            className="w-fit rounded-md border border-cyan-500/40 bg-cyan-950/40 px-5 py-2.5 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                        >
                            {pwSaving ? "Updating…" : "Update password"}
                        </button>
                    </div>
                ) : (
                    <p
                        data-testid="password-managed-note"
                        className="mt-4 font-mono text-[11px] text-slate-500"
                    >
                        You sign in with Google — your password is managed by your identity
                        provider.
                    </p>
                )}
            </div>
        </div>
    );
}
